import React from 'react';
import { TooltipRenderProps } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Button } from '../../form/button/button';
import { IconNext } from '../../icons/next';
import { OnboardingFooter } from '../footer/onboarding-footer';
import { IconClose } from '../../icons/close';
import { useRootStore } from '../../../../providers/root-store-provider';
import { OnboardingCaseEnum } from '../../../../models/enums/onboarding-case-enum';
import { AddTradesOnboardingSubcaseEnum } from '../../../../models/enums/add-trades-onboarding-subcase-enum';

type CustomTooltip = TooltipRenderProps & {
    isLast: boolean;
};

export const CustomTooltip: React.FC<CustomTooltip> = ({
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    isLast,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { onboardingStore } = useRootStore();

    // Use "Rozumím" for import sequence last step, "Dokončit" for others
    const getFinishButtonText = () => {
        if (
            isLast &&
            onboardingStore.case === OnboardingCaseEnum.AddTrades &&
            onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import
        ) {
            return 'Rozumím';
        }
        return t('layout.onboarding.finish');
    };

    return (
        <div
            {...tooltipProps}
            className="relative z-[100001] min-w-[28rem] max-w-[40rem] rounded-medium p-medium text-vlarge leading-vlarge text-text"
            style={{ backgroundColor: theme.palette.background.header }}
        >
            <div
                {...closeProps}
                className="absolute top-[1rem] right-[1rem] cursor-pointer [&_i]:text-[var(--onboarding-close-color)] [&_i]:transition-colors hover:[&_i]:text-status-negative"
                style={
                    {
                        '--onboarding-close-color': theme.palette.color.default.opacity.low,
                    } as React.CSSProperties
                }
            >
                <IconClose />
            </div>
            <div>{step.content}</div>

            {!step.hideFooter && (
                <OnboardingFooter>
                    {step.showSkipButton && (
                        <Button
                            {...closeProps}
                            variant="default"
                            variation={'outlined'}
                            size={{ xs: 'vsmall', vl: 'medium' }}
                        >
                            {t('layout.onboarding.skip')}
                        </Button>
                    )}
                    {!step.hideBackButton && (
                        <Button
                            {...backProps}
                            variant="default"
                            variation={'outlined'}
                            size={{ xs: 'vsmall', vl: 'medium' }}
                        >
                            {t('layout.onboarding.previous')}
                        </Button>
                    )}
                    <Button
                        {...primaryProps}
                        variant="primary"
                        size={{ xs: 'xsmall', vl: 'medium' }}
                        iconGap={'vsmall'}
                        icon={<IconNext />}
                    >
                        {isLast ? getFinishButtonText() : t('layout.onboarding.next')}
                    </Button>
                </OnboardingFooter>
            )}
        </div>
    );
};
